/**
 * AdminHeroReview — the NEEDS HERO REVIEW queue panel.
 *
 * Makes the publish gate visible: every dossier currently held because
 * its hero image is missing / failed generation, plus any PUBLIC dossier
 * whose hero file has gone missing (fallback artwork in use).
 *
 * Per-item actions: Generate / Regenerate hero (background pipeline with
 * live polling), Preview hero, Approve & publish (only when a hero file
 * exists — the gate is never bypassable), Keep held (acknowledge).
 * After each generation the panel runs the 4-point verification through
 * the real public API: image HTTP 200 · archive card · detail page ·
 * audit PASS.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ImageOff, RefreshCw, Loader2, Eye, CheckCircle2, XCircle,
  ShieldCheck, Archive, Clock, Sparkles, Radar, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import {
  adminHeroQueue, adminHeroGenerate, adminHeroStatus,
  adminHeroVerify, adminHeroApprove, adminHeroKeepHeld,
  adminHeroAuditRun, adminHeroAuditLatest,
} from "../../lib/api";

const API = process.env.REACT_APP_BACKEND_URL || "";

const _fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

const VerifyRow = ({ label, ok }) => (
  <span className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.15em]"
        style={{ color: ok ? "#3FB950" : "#D92D20" }}>
    {ok ? <CheckCircle2 size={10} /> : <XCircle size={10} />} {label}
  </span>
);

const HeroCard = ({ item, token, userToken, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [genState, setGenState] = useState(item.hero?.state || null);
  const [verify, setVerify] = useState(null);
  const [preview, setPreview] = useState(false);
  const [cacheBust, setCacheBust] = useState(0);
  const pollRef = useRef(null);

  const slug = item.slug;
  const hasFile = !!item.hero?.hero_file_exists;
  const imageUrl = item.hero?.image_url;
  const held = item.dossier_status === "needs_hero_review";

  useEffect(() => () => clearInterval(pollRef.current), []);

  const runVerify = useCallback(async () => {
    try {
      const v = await adminHeroVerify(token, slug, userToken);
      setVerify(v);
      if (v.all_pass) toast.success(`${slug} — verification PASS`);
      else toast.warning(`${slug} — verification incomplete`);
    } catch { toast.error("Verification call failed"); }
  }, [token, userToken, slug]);

  const generate = useCallback(async () => {
    setBusy(true); setVerify(null);
    try {
      await adminHeroGenerate(token, slug, userToken);
      setGenState("running");
      toast.info(`Hero generation started for ${slug} (~60s)`);
      pollRef.current = setInterval(async () => {
        try {
          const st = await adminHeroStatus(token, slug, userToken);
          if (st.state === "running") return;
          clearInterval(pollRef.current);
          setGenState(st.state);
          setBusy(false);
          setCacheBust(Date.now());
          if (st.state === "done") {
            toast.success(`Hero ready — ${slug} auto-published`);
            await runVerify();
            onChanged();
          } else {
            toast.error(`Generation failed for ${slug}: ${st.hero_review?.error || "see queue"}`);
            onChanged();
          }
        } catch { /* keep polling */ }
      }, 6000);
    } catch (e) {
      setBusy(false);
      toast.error(e?.response?.data?.detail || "Could not start generation");
    }
  }, [token, userToken, slug, onChanged, runVerify]);

  const approve = useCallback(async () => {
    setBusy(true);
    try {
      await adminHeroApprove(token, slug, userToken);
      toast.success(`${slug} approved & published`);
      await runVerify();
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally { setBusy(false); }
  }, [token, userToken, slug, onChanged, runVerify]);

  const keepHeld = useCallback(async () => {
    setBusy(true);
    try {
      await adminHeroKeepHeld(token, slug, userToken);
      toast.info(`${slug} kept held (acknowledged)`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Keep-held failed");
    } finally { setBusy(false); }
  }, [token, userToken, slug, onChanged]);

  const running = busy || genState === "running";
  const btnCls = "inline-flex items-center gap-1.5 px-3 py-1.5 border font-mono text-[9px] uppercase tracking-[0.22em] transition-colors disabled:opacity-40 disabled:cursor-not-allowed";

  return (
    <div
      data-testid={`hero-review-item-${slug}`}
      className={`border border-[#2D2A26] bg-[#0F0F0F] p-4 ${item.acknowledged ? "opacity-60" : ""}`}
    >
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-heading text-base text-[#E4DFD3]" data-testid={`hero-review-title-${slug}`}>
              {item.title || slug}
            </span>
            <span className="font-mono text-[9px] uppercase tracking-[0.25em] px-2 py-0.5 border"
                  style={held
                    ? { color: "#E0A526", borderColor: "#E0A526" }
                    : { color: "#D92D20", borderColor: "#D92D20" }}
                  data-testid={`hero-review-state-${slug}`}>
              {held ? "HELD · NEEDS HERO REVIEW" : "PUBLIC · NO HERO FILE"}
            </span>
            {item.acknowledged && (
              <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#7B7168]">acknowledged</span>
            )}
          </div>
          <div className="mt-1 font-mono text-[10px] text-[#A39E93] flex flex-wrap gap-x-4 gap-y-1">
            <span>REF: {slug}</span>
            <span>CAT: {item.category || "—"}</span>
            <span className="inline-flex items-center gap-1"><Clock size={10} /> created {_fmt(item.created_at)}</span>
            {item.updated_at && <span>updated {_fmt(item.updated_at)}</span>}
          </div>
          <div className="mt-2 font-mono text-[10px] text-[#D9A56C]" data-testid={`hero-review-reason-${slug}`}>
            // {item.reason}
          </div>
          <div className="mt-1 font-mono text-[10px] text-[#7B7168]">
            hero state: {hasFile ? `file present (${imageUrl})` : "no file"}{genState ? ` · job: ${genState}` : ""}
          </div>
          {verify && (
            <div className="mt-2 flex flex-wrap gap-3" data-testid={`hero-review-verify-${slug}`}>
              <VerifyRow label="image 200" ok={verify.checks.image_http_200} />
              <VerifyRow label="archive card" ok={verify.checks.archive_card_renders} />
              <VerifyRow label="detail page" ok={verify.checks.detail_page_renders} />
              <VerifyRow label="audit pass" ok={verify.checks.audit_pass} />
            </div>
          )}
        </div>
      </div>

      {preview && imageUrl && (
        <div className="mt-3 border border-[#2D2A26]" data-testid={`hero-review-preview-${slug}`}>
          <img src={`${API}${imageUrl}?v=${cacheBust}`} alt={`Hero for ${slug}`}
               className="w-full max-h-72 object-cover" />
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" onClick={generate} disabled={running}
                data-testid={`hero-generate-btn-${slug}`}
                className={`${btnCls} border-[#D92D20] text-[#D92D20] hover:bg-[#D92D20] hover:text-[#0a0907]`}>
          {running ? <Loader2 size={11} className="animate-spin" /> : (hasFile ? <RefreshCw size={11} /> : <Sparkles size={11} />)}
          {running ? "Generating…" : hasFile ? "Regenerate hero" : "Generate hero"}
        </button>
        <button type="button" onClick={() => setPreview((p) => !p)} disabled={!hasFile}
                data-testid={`hero-preview-btn-${slug}`}
                className={`${btnCls} border-[#454038] text-[#E4DFD3] hover:border-[#E0A526] hover:text-[#E0A526]`}>
          <Eye size={11} /> {preview ? "Hide preview" : "Preview hero"}
        </button>
        {held && (
          <button type="button" onClick={approve} disabled={busy || !hasFile}
                  title={hasFile ? "Publish this dossier" : "Generate a hero first — the gate cannot be bypassed"}
                  data-testid={`hero-approve-btn-${slug}`}
                  className={`${btnCls} border-[#3FB950] text-[#3FB950] hover:bg-[#3FB950] hover:text-[#0a0907]`}>
            <ShieldCheck size={11} /> Approve &amp; publish
          </button>
        )}
        {held && !item.acknowledged && (
          <button type="button" onClick={keepHeld} disabled={busy}
                  data-testid={`hero-keep-held-btn-${slug}`}
                  className={`${btnCls} border-[#454038] text-[#A39E93] hover:border-[#7B7168] hover:text-[#E4DFD3]`}>
            <Archive size={11} /> Keep held
          </button>
        )}
        <button type="button" onClick={runVerify} disabled={busy || !hasFile}
                data-testid={`hero-verify-btn-${slug}`}
                className={`${btnCls} border-[#454038] text-[#A39E93] hover:border-[#3FB950] hover:text-[#3FB950]`}>
          <CheckCircle2 size={11} /> Verify
        </button>
      </div>
    </div>
  );
};

const AuditSummaryBar = ({ token, userToken }) => {
  const [latest, setLatest] = useState(null);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    try { setLatest(await adminHeroAuditLatest(token, userToken)); }
    catch { /* silent */ }
  }, [token, userToken]);

  useEffect(() => { load(); }, [load]);

  const runNow = useCallback(async () => {
    setRunning(true);
    try {
      const run = await adminHeroAuditRun(token, userToken);
      if (run.all_pass) toast.success(`Audit complete — ${run.total} dossiers, ALL PASS`);
      else toast.warning(`Audit complete — regressions: ${run.changes?.regressions?.length || 0}${run.email_sent ? " · alert emailed" : ""}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Audit run failed");
    } finally { setRunning(false); }
  }, [token, userToken, load]);

  const counts = latest?.counts || {};
  const failures = latest?.failures || [];
  const changes = latest?.changes || {};

  return (
    <div className="border border-[#2D2A26] bg-[#0F0F0F] p-4 mb-6" data-testid="hero-audit-summary">
      <div className="flex flex-wrap items-center gap-3">
        <Radar size={14} className="text-[#D92D20]" />
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#E4DFD3]">Fleet Audit</span>
        {latest?.available ? (
          <>
            <span className="font-mono text-[10px] text-[#7B7168]" data-testid="hero-audit-last-run">
              last run {_fmt(latest.run_at)} · {latest.trigger} · {latest.target}
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 border"
                  style={latest.all_pass
                    ? { color: "#3FB950", borderColor: "#3FB950" }
                    : { color: "#D92D20", borderColor: "#D92D20" }}
                  data-testid="hero-audit-pass-count">
              {counts.PASS || 0}/{latest.total} PASS
            </span>
          </>
        ) : (
          <span className="font-mono text-[10px] text-[#7B7168]">no audit recorded yet</span>
        )}
        <button type="button" onClick={runNow} disabled={running}
                data-testid="hero-audit-run-btn"
                className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#D92D20] text-[#D92D20] hover:bg-[#D92D20] hover:text-[#0a0907] font-mono text-[9px] uppercase tracking-[0.22em] disabled:opacity-40">
          {running ? <Loader2 size={11} className="animate-spin" /> : <Radar size={11} />}
          {running ? "Auditing…" : "Run audit now"}
        </button>
      </div>

      {latest?.available && failures.length > 0 && (
        <div className="mt-3 space-y-1" data-testid="hero-audit-failures">
          {failures.map((f) => (
            <div key={f.slug} className="font-mono text-[10px] text-[#E0A526]">
              ✗ {f.slug} — {f.status}
            </div>
          ))}
        </div>
      )}

      {latest?.available && ((changes.regressions?.length || 0) > 0 || (changes.recoveries?.length || 0) > 0) && (
        <div className="mt-3 border-t border-[#2D2A26] pt-3 space-y-1" data-testid="hero-audit-changes">
          <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#7B7168]">changes since previous audit</div>
          {(changes.regressions || []).map((c) => (
            <div key={`reg-${c.slug}`} className="font-mono text-[10px] text-[#D92D20] flex items-center gap-1.5">
              <XCircle size={10} /> {c.slug}: {c.previous_status} <ArrowRight size={9} /> {c.current_status}
            </div>
          ))}
          {(changes.recoveries || []).map((c) => (
            <div key={`rec-${c.slug}`} className="font-mono text-[10px] text-[#3FB950] flex items-center gap-1.5">
              <CheckCircle2 size={10} /> {c.slug}: {c.previous_status} <ArrowRight size={9} /> PASS
            </div>
          ))}
        </div>
      )}

      {latest?.available && latest.alerted && (
        <div className="mt-2 font-mono text-[9px] uppercase tracking-[0.2em] text-[#E0A526]">
          {latest.email_sent ? "· alert digest emailed to admins" : "· regressions detected (email skipped/disabled)"}
        </div>
      )}
    </div>
  );
};

export const AdminHeroReview = ({ token, userToken }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const d = await adminHeroQueue(token, userToken);
      setData(d);
    } catch { toast.error("Could not load hero review queue"); }
    finally { setLoading(false); }
  }, [token, userToken]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-[1100px] mx-auto px-4 md:px-8 py-8" data-testid="hero-review-panel">
      <div className="flex flex-wrap items-center gap-3 mb-2">
        <ImageOff size={16} className="text-[#D92D20]" />
        <h1 className="font-display text-2xl text-[#E4DFD3] tracking-tight">Needs Hero Review</h1>
        {data && (
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] px-2 py-1 border border-[#E0A526] text-[#E0A526]"
                data-testid="hero-review-count">
            {data.count} in queue · {data.held_count} held
          </span>
        )}
        <button type="button" onClick={() => { setLoading(true); load(); }}
                data-testid="hero-review-refresh-btn"
                className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#454038] text-[#A39E93] hover:border-[#D92D20] hover:text-[#E4DFD3] font-mono text-[9px] uppercase tracking-[0.22em]">
          <RefreshCw size={11} /> Refresh
        </button>
      </div>
      <p className="font-mono text-[10px] text-[#7B7168] uppercase tracking-[0.2em] mb-6">
        // Publish gate — no dossier goes public without case-specific artwork
      </p>

      <AuditSummaryBar token={token} userToken={userToken} />

      {loading && (
        <div className="flex items-center gap-2 text-[#A39E93] font-mono text-xs" data-testid="hero-review-loading">
          <Loader2 size={14} className="animate-spin" /> Loading queue…
        </div>
      )}

      {!loading && data && data.count === 0 && (
        <div className="border border-[#2D2A26] bg-[#0F0F0F] p-8 text-center" data-testid="hero-review-empty">
          <CheckCircle2 size={22} className="mx-auto text-[#3FB950] mb-3" />
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#3FB950]">All clear</div>
          <div className="mt-2 font-mono text-[10px] text-[#7B7168]">
            Every public dossier has a story-specific hero. Nothing is held.
          </div>
        </div>
      )}

      {!loading && data && data.count > 0 && (
        <div className="space-y-4" data-testid="hero-review-list">
          {data.items.map((item) => (
            <HeroCard key={item.slug} item={item} token={token}
                      userToken={userToken} onChanged={load} />
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminHeroReview;
